import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FiltrosTematicos from './FiltrosTematicos';
import AdSlot from './AdSlot';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../config';
import './Indice.css';

const Indice = ({ userAuth, stats, setTema, tema }) => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    // Todos los artículos (para búsqueda global)
    const [todosLosArticulos, setTodosLosArticulos] = useState([]);
    // 8 artículos más recientes para portada
    const [ultimosArticulos, setUltimosArticulos] = useState([]);
    const [comentariosRecientes, setComentariosRecientes] = useState([]);
    const [loadingContent, setLoadingContent] = useState(true);
    const [filtroTematico, setFiltroTematico] = useState('todos');

    // --- BUSCADOR GLOBAL ---
    const [busquedaGlobal, setBusquedaGlobal] = useState('');
    const [mostrarResultados, setMostrarResultados] = useState(false);
    const buscadorRef = useRef(null);

    // Asegurar título unificado en la portada para Google Analytics 4 y SEO
    useEffect(() => {
        document.title = 'Expediente X Granaíno | Investigación OVNI y Fenómenos Paranormales en Granada';
    }, []);

    // Cerrar resultados al hacer clic fuera
    useEffect(() => {
        const handleClickFuera = (e) => {
            if (buscadorRef.current && !buscadorRef.current.contains(e.target)) {
                setMostrarResultados(false);
            }
        };
        document.addEventListener('mousedown', handleClickFuera);
        return () => document.removeEventListener('mousedown', handleClickFuera);
    }, []);

    const resultadosBusqueda = busquedaGlobal.trim().length >= 3
        ? todosLosArticulos.filter(art => {
            const q = busquedaGlobal.trim().toLowerCase();
            return (
                (art.titulo || '').toLowerCase().includes(q) ||
                (art.contenido || '').toLowerCase().includes(q)
            );
        }).slice(0, 15)
        : [];

    const seccionColors = {
        expediente: { bg: '#1e3a2b', label: 'EXPEDIENTE' },
        noticia: { bg: '#1e293b', label: 'NOTICIA' },
        caso: { bg: '#7f1d1d', label: 'TRUE CRIME' },
        misterio: { bg: '#7c2d12', label: 'MIST. HIST.' },
    };

    useEffect(() => {
        const fetchHomeData = async () => {
            try {
                setLoadingContent(true);
                const [resExp, resNot, resCasos, resMisterios, resComs] = await Promise.allSettled([
                    axios.get(`${API_BASE_URL}/api/expedientes`),
                    axios.get(`${API_BASE_URL}/api/galeria/noticias-publicas`),
                    axios.get(`${API_BASE_URL}/api/casos`),
                    axios.get(`${API_BASE_URL}/api/misterios-historicos`),
                    axios.get(`${API_BASE_URL}/api/comentarios/recientes`)
                ]);

                let articulosUnificados = [];

                // 1. EXPEDIENTES
                if (resExp.status === 'fulfilled' && Array.isArray(resExp.value.data)) {
                    resExp.value.data.forEach(item => {
                        const img = item.imagen_url 
                            ? (item.imagen_url.startsWith('http') ? item.imagen_url : `${API_BASE_URL}/imagenes/${item.imagen_url}`)
                            : null;
                        articulosUnificados.push({
                            id: item.id,
                            titulo: item.titulo || 'Sin título',
                            contenido: item.contenido || item.cuerpo || '',
                            imagen: img,
                            imagen_posicion: item.imagen_posicion || 'center center',
                            seccion: 'expediente',
                            seccionLabel: 'EXPEDIENTE',
                            seccionColor: '#1e3a2b',
                            fecha: item.fecha,
                            timestamp: new Date(item.fecha || 0).getTime(),
                            autor: item.usuario_nombre || 'José Moreno',
                            link: `/expedientes/${item.id}`
                        });
                    });
                }

                // 2. NOTICIAS
                if (resNot.status === 'fulfilled' && Array.isArray(resNot.value.data)) {
                    resNot.value.data.forEach(item => {
                        const img = item.imagen_url 
                            ? (item.imagen_url.startsWith('http') ? item.imagen_url : `${API_BASE_URL}/imagenes/${item.imagen_url.split('/').pop()}`)
                            : null;
                        articulosUnificados.push({
                            id: item.id,
                            titulo: item.titulo || 'Noticia de última hora',
                            contenido: item.cuerpo || item.contenido || '',
                            imagen: img,
                            imagen_posicion: item.imagen_posicion || 'center center',
                            seccion: 'noticia',
                            seccionLabel: 'NOTICIA',
                            seccionColor: '#1e293b',
                            fecha: item.fecha,
                            timestamp: new Date(item.fecha || 0).getTime(),
                            autor: 'Redacción',
                            link: `/noticias/${item.id}`
                        });
                    });
                }

                // 3. TRUE CRIME (CASOS ABIERTOS)
                if (resCasos.status === 'fulfilled' && Array.isArray(resCasos.value.data)) {
                    resCasos.value.data.forEach(item => {
                        const img = item.imagen_url 
                            ? (item.imagen_url.startsWith('http') ? item.imagen_url : `${API_BASE_URL}/imagenes/${item.imagen_url}`)
                            : null;
                        const tit = (language === 'en' && item.titulo_en) ? item.titulo_en : item.titulo;
                        const desc = (language === 'en' && item.contenido_en) ? item.contenido_en : item.contenido;
                        articulosUnificados.push({
                            id: item.id,
                            titulo: tit || 'Caso Abierto',
                            contenido: desc || '',
                            imagen: img,
                            imagen_posicion: item.imagen_posicion || 'center center',
                            seccion: 'caso',
                            seccionLabel: 'TRUE CRIME',
                            seccionColor: '#b91c1c',
                            fecha: item.fecha,
                            timestamp: new Date(item.fecha || 0).getTime(),
                            autor: 'José Moreno',
                            link: `/casos-abiertos/${item.id}`
                        });
                    });
                }

                // 4. MISTERIOS HISTÓRICOS
                if (resMisterios.status === 'fulfilled' && Array.isArray(resMisterios.value.data)) {
                    resMisterios.value.data.forEach(item => {
                        const img = item.imagen_url 
                            ? (item.imagen_url.startsWith('http') ? item.imagen_url : `${API_BASE_URL}/imagenes/${item.imagen_url}`)
                            : null;
                        const tit = (language === 'en' && item.titulo_en) ? item.titulo_en : item.titulo;
                        const desc = (language === 'en' && item.contenido_en) ? item.contenido_en : item.contenido;
                        articulosUnificados.push({
                            id: item.id,
                            titulo: tit || 'Misterio Histórico',
                            contenido: desc || '',
                            imagen: img,
                            imagen_posicion: item.imagen_posicion || 'center center',
                            seccion: 'misterio',
                            seccionLabel: 'MISTERIOS HISTÓRICOS',
                            seccionColor: '#9a3412',
                            fecha: item.fecha,
                            timestamp: new Date(item.fecha || 0).getTime(),
                            autor: 'José Moreno',
                            link: `/misterios-historicos/${item.id}`
                        });
                    });
                }

                // Ordenar por fecha cronológica descendente (los más recientes primero)
                articulosUnificados.sort((a, b) => b.timestamp - a.timestamp);

                // Guardar TODOS para el buscador global
                setTodosLosArticulos(articulosUnificados);

                // Tomar los 8 más recientes para la portada principal
                setUltimosArticulos(articulosUnificados.slice(0, 8));

                if (resComs.status === 'fulfilled' && Array.isArray(resComs.value.data)) {
                    setComentariosRecientes(resComs.value.data.slice(0, 4));
                }
            } catch (err) {
                console.error("Error loading home page content:", err);
            } finally {
                setLoadingContent(false);
            }
        };
        fetchHomeData();
    }, [language]);

    // Filtrar si el usuario pulsa algún filtro temático
    const articulosAMostrar = filtroTematico === 'todos'
        ? ultimosArticulos
        : filtroTematico === 'ovnis'
            ? ultimosArticulos.filter(a => a.seccion === 'expediente')
            : filtroTematico === 'noticias'
                ? ultimosArticulos.filter(a => a.seccion === 'noticia')
                : filtroTematico === 'cronica_negra'
                    ? ultimosArticulos.filter(a => a.seccion === 'caso')
                    : filtroTematico === 'misterios'
                        ? ultimosArticulos.filter(a => a.seccion === 'misterio')
                        : ultimosArticulos;

    const articuloPrincipal = articulosAMostrar.length > 0 ? articulosAMostrar[0] : null;
    const articulosSecundarios = articulosAMostrar.length > 1 ? articulosAMostrar.slice(1, 8) : [];

    const limpiarSnippet = (texto, maxLen = 140) => {
        if (!texto) return '';
        const sinHtml = texto.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');
        if (sinHtml.length <= maxLen) return sinHtml;
        return sinHtml.substring(0, maxLen).trim() + '...';
    };

    return (
        <div className="indice-editorial-container">
            {/* BARRA DE FILTROS TEMÁTICOS EDITORIAL (ARRIBA DEL TODO) */}
            <FiltrosTematicos filtroActivo={filtroTematico} onFiltroChange={setFiltroTematico} />

            {/* ── BUSCADOR GLOBAL DE ARTÍCULOS ── */}
            <div className="buscador-global-wrap" ref={buscadorRef}>
                {/* GUIÑO PARA VISITANTES DE TIKTOK / YOUTUBE / REDES */}
                <div
                    style={{
                        background: 'rgba(30, 58, 43, 0.06)',
                        border: '1px solid rgba(30, 58, 43, 0.25)',
                        borderLeft: '3px solid #1e3a2b',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        marginBottom: '8px',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: '0.82rem',
                        lineHeight: 1.5,
                        color: '#1A1A1A',
                        textAlign: 'left'
                    }}
                >
                    {language === 'en' ? (
                        <>
                            🔎 <strong>Coming from TikTok, YouTube or social media?</strong> Search by keyword for the case from the video or try your luck at{' '}
                            <Link to="/la-ruleta" style={{ color: '#1e3a2b', fontWeight: 700, textDecoration: 'underline' }}>🎰 The Bunker Roulette</Link>
                        </>
                    ) : (
                        <>
                            🔎 <strong>¿Vienes de TikTok, YouTube o Redes?</strong> Busca aquí por palabra clave el expediente del vídeo o prueba suerte en{' '}
                            <Link to="/la-ruleta" style={{ color: '#1e3a2b', fontWeight: 700, textDecoration: 'underline' }}>🎰 La Ruleta del Búnker</Link>
                        </>
                    )}
                </div>
                <div className="buscador-global-inner">
                    <span className="buscador-global-icon">🔍</span>
                    <input
                        className="buscador-global-input"
                        type="text"
                        placeholder={language === 'en' ? 'Search all articles... (poltergeist, OVNI, atarfe...)' : 'Buscar en todos los artículos... (poltergeist, OVNI, Atarfe...)'}
                        value={busquedaGlobal}
                        onChange={e => {
                            setBusquedaGlobal(e.target.value);
                            setMostrarResultados(true);
                        }}
                        onFocus={() => setMostrarResultados(true)}
                    />
                    {busquedaGlobal && (
                        <button
                            className="buscador-global-clear"
                            onClick={() => { setBusquedaGlobal(''); setMostrarResultados(false); }}
                            type="button"
                        >✖</button>
                    )}
                </div>

                {/* DROPDOWN DE RESULTADOS */}
                {mostrarResultados && busquedaGlobal.trim().length >= 3 && (
                    <div className="buscador-global-results">
                        {resultadosBusqueda.length === 0 ? (
                            <div className="buscador-global-empty">
                                {language === 'en' ? 'No results found for' : 'Sin resultados para'} "<strong>{busquedaGlobal}</strong>"
                            </div>
                        ) : (
                            <>
                                <div className="buscador-global-count">
                                    {resultadosBusqueda.length} {resultadosBusqueda.length === 1 ? (language === 'en' ? 'result' : 'resultado') : (language === 'en' ? 'results' : 'resultados')} para "<strong>{busquedaGlobal}</strong>"
                                </div>
                                {resultadosBusqueda.map(art => (
                                    <div
                                        key={`${art.seccion}-${art.id}`}
                                        className="buscador-global-item"
                                        onClick={() => {
                                            navigate(art.link);
                                            setBusquedaGlobal('');
                                            setMostrarResultados(false);
                                        }}
                                    >
                                        {art.imagen && (
                                            <img
                                                src={art.imagen}
                                                alt=""
                                                className="buscador-global-thumb"
                                                onError={e => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                        <div className="buscador-global-item-info">
                                            <span
                                                className="buscador-global-badge"
                                                style={{ background: (seccionColors[art.seccion] || {}).bg || '#333' }}
                                            >
                                                {(seccionColors[art.seccion] || {}).label || art.seccionLabel}
                                            </span>
                                            <span className="buscador-global-title">{art.titulo}</span>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* BARRA DE FECHA Y EDICIÓN */}
            <div className="editorial-edition-bar">
                <span className="editorial-edition-date">
                    {new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'es-ES', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                    }).toUpperCase()}
                </span>
                <span className="editorial-edition-tag">
                    {language === 'en' ? 'EDITION ARCHIVE · UNEXPLAINED DOSSIERS' : 'EDICIÓN DIGITAL · ARCHIVO DEL MISTERIO'}
                </span>
            </div>

            {/* SECCIÓN PRINCIPAL DE PORTADA (8 ARTÍCULOS) */}
            <section className="editorial-frontpage">
                <div className="editorial-section-header">
                    <h2 className="editorial-section-title">
                        {language === 'en' ? 'LATEST EDITIONS' : 'ÚLTIMAS PUBLICACIONES'}
                    </h2>
                    <span className="editorial-section-subtitle">
                        {language === 'en' ? 'The 8 most recent investigations and chronicles' : 'Los 8 artículos más recientes del archivo'}
                    </span>
                </div>

                {loadingContent ? (
                    <div className="editorial-loading">
                        <p>{language === 'en' ? 'Loading latest investigations...' : 'Cargando las últimas publicaciones...'}</p>
                    </div>
                ) : articulosAMostrar.length === 0 ? (
                    <div className="editorial-empty">
                        <p>{language === 'en' ? 'No articles found in this category.' : 'No se han encontrado publicaciones en esta sección.'}</p>
                    </div>
                ) : (
                    <>
                        {/* ARTÍCULO PRINCIPAL (#1 DE 8 - FORMATO GRAN TITULAR) */}
                        {articuloPrincipal && (
                            <article 
                                className="editorial-lead-story" 
                                onClick={() => navigate(articuloPrincipal.link)}
                            >
                                <div className="lead-story-image-wrap">
                                    {articuloPrincipal.imagen ? (
                                        <img 
                                            src={articuloPrincipal.imagen} 
                                            alt={articuloPrincipal.titulo} 
                                            loading="eager"
                                            style={{ objectPosition: articuloPrincipal.imagen_posicion || 'center center' }}
                                        />
                                    ) : (
                                        <div className="lead-story-placeholder">
                                            <span>DOCUMENTACIÓN FOTOGRÁFICA</span>
                                        </div>
                                    )}
                                </div>

                                <div className="lead-story-content">
                                    <span className="lead-story-kicker">
                                        {articuloPrincipal.seccionLabel} · REPORTAJE PRINCIPAL
                                    </span>
                                    <h3 className="lead-story-title">
                                        <Link to={articuloPrincipal.link} style={{ color: 'inherit', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                                            {articuloPrincipal.titulo}
                                        </Link>
                                    </h3>
                                    <p className="lead-story-excerpt">
                                        {limpiarSnippet(articuloPrincipal.contenido, 260)}
                                    </p>
                                    <div className="lead-story-meta">
                                        <span className="lead-story-author">Por {articuloPrincipal.autor}</span>
                                        <span className="lead-story-divider">·</span>
                                        <span className="lead-story-date">
                                            {articuloPrincipal.fecha ? new Date(articuloPrincipal.fecha).toLocaleDateString(language === 'en' ? 'en-GB' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                                        </span>
                                    </div>
                                    <div className="lead-story-cta">
                                        <span className="btn-read-lead">
                                            {language === 'en' ? 'READ FULL DOSSIER' : 'LEER ARTÍCULO COMPLETO'} →
                                        </span>
                                    </div>
                                </div>
                            </article>
                        )}

                        {/* ARTÍCULOS SECUNDARIOS (#2 A #8 - GRID EDITORIAL DE 3 COLUMNAS) */}
                        {articulosSecundarios.length > 0 && (
                            <div className="editorial-articles-grid">
                                {articulosSecundarios.map((art) => (
                                    <article 
                                        key={`${art.seccion}-${art.id}`} 
                                        className="editorial-card"
                                        onClick={() => navigate(art.link)}
                                    >
                                        <div className="editorial-card-image-wrap">
                                            {art.imagen ? (
                                                <img 
                                                    src={art.imagen} 
                                                    alt={art.titulo} 
                                                    loading="lazy" 
                                                    style={{ objectPosition: art.imagen_posicion || 'center center' }}
                                                />
                                            ) : (
                                                <div className="editorial-card-placeholder">
                                                    <span>{art.seccionLabel}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="editorial-card-body">
                                            <h4 className="editorial-card-title">
                                                <Link to={art.link} style={{ color: 'inherit', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                                                    {art.titulo}
                                                </Link>
                                            </h4>
                                            <p className="editorial-card-excerpt">
                                                {limpiarSnippet(art.contenido, 110)}
                                            </p>
                                            <div className="editorial-card-meta">
                                                <span className="editorial-card-author">{art.autor}</span>
                                                <span className="editorial-card-divider">·</span>
                                                <span className="editorial-card-date">
                                                    {art.fecha ? new Date(art.fecha).toLocaleDateString(language === 'en' ? 'en-GB' : 'es-ES', { day: 'numeric', month: 'short' }) : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* SECCIÓN: NOTAS Y TRANSMISIONES RECIENTES DE LECTORES */}
            {comentariosRecientes.length > 0 && (
                <section className="editorial-transmissions-section">
                    <div className="editorial-section-header">
                        <h3 className="editorial-section-title">
                            {language === 'en' ? 'READERS & AGENTS TRANSMISSIONS' : 'TRANSMISIONES Y COMENTARIOS DE LECTORES'}
                        </h3>
                    </div>

                    <div className="editorial-comments-grid">
                        {comentariosRecientes.map((c) => {
                            const parts = (c.item_key || '').split('-');
                            const tipo = parts[0];
                            const id = parts[1];
                            const srcParam = tipo === 'exp' ? 'expedientes' : tipo === 'caso' ? 'casos' : tipo === 'misterio' ? 'misterios' : 'noticias';
                            const linkUrl = tipo === 'exp' ? `/expedientes/${id}` : tipo === 'caso' ? `/casos-abiertos/${id}` : tipo === 'misterio' ? `/misterios-historicos/${id}` : `/noticias/${id}`;

                            return (
                                <div 
                                    key={c.id} 
                                    className="editorial-comment-card"
                                    onClick={() => navigate(linkUrl)}
                                >
                                    <div className="comment-card-header">
                                        <span className="comment-author">
                                            {c.agente ? c.agente.toUpperCase() : 'LECTOR'}
                                        </span>
                                        <span className="comment-date">
                                            {new Date(c.fecha).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <p className="comment-text">"{c.mensaje}"</p>
                                    <div className="comment-target">
                                        <span>En: </span>
                                        <span className="comment-target-title">
                                            {c.titulo_articulo || 'Ver expediente'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* TARJETA EDITORIAL DEL FUNDADOR */}
            <section className="editorial-author-block">
                <div className="author-block-avatar">
                    <img 
                        src="/jose-moreno-investigador.jpg" 
                        alt="José Moreno Jiménez" 
                    />
                </div>
                <div className="author-block-info">
                    <span className="author-block-role">
                        {language === 'en' ? 'CREATOR & WEB DEVELOPER' : 'CREADOR Y DESARROLLADOR DE LA PLATAFORMA'}
                    </span>
                    <h4 className="author-block-name">José Moreno Jiménez</h4>
                    <p className="author-block-bio">
                        {language === 'en' 
                            ? 'Specialist in ufology, historical anomalies, and unsolved criminal cases in southern Spain. Directing the independent documentary archive Expediente X Granaíno.' 
                            : 'Especialista en ufología, anomalías históricas y casos sin resolver en el sur de España. Creador y gestor del archivo documental independiente Expediente X Granaíno.'}
                    </p>
                    <Link to="/sobre-nosotros" className="author-block-link">
                        {language === 'en' ? 'Read full author dossier →' : 'Conocer más sobre el proyecto y trayectoria →'}
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Indice;